import {
  Component,
  Host,
  h,
  Prop,
  Watch,
  Element,
  State,
  Event,
  EventEmitter,
} from '@stencil/core';
// @ts-ignore
import CalendarDates from 'calendar-dates';
const calendarDates = new CalendarDates();
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as Sentry from '@sentry/browser';

dayjs.extend(customParseFormat);

/**
 * Using this function to fix each bad locale as it appears in Sentry.
 * We might need to find a better long-term solution if more bad locales appear.
 * So far we have seen:
 * - "en-US@posix"
 * - "united states"
 * - "united states-UNITED STATES"
 * - "hi-in, hi, en-us, en"
 */
const getValidLocale = (locale: string) => {
  // No locale, return en-US
  if (!locale) return 'en-US';
  // Locale contains 'en-us', return en-US
  if (locale.toLowerCase().includes('en-us')) return 'en-US';

  // Remove '@posix', '-UNITED STATES' suffix
  locale = locale.replace(/(@posix\b|-UNITED STATES\b)/gi, '');
  // Fix 'united states' locale
  locale = locale.replace(/united states/i, 'en-US');
  return locale;
};

// Array of month names (used in month <select> field).
const calendarMonths = [...Array(12).keys()].map(key => {
  try {
    let locale = getValidLocale(navigator.language);
    return new Date(0, key).toLocaleString(locale, { month: 'long' });
  } catch (error) {
    Sentry.captureException(error, { extra: { language: navigator.language } });
  }
});

// If input value is blank, set it back to the placeholder value (which is the last value specified).
function setCalendarDefaultValue(event: any) {
  const target = event.currentTarget;
  if (!target.value) {
    target.value = target.placeholder;
    handleInputChange(event);
  } else {
    // If the current input has a value which is not valid, then reset that value back to the value stored in `date`.
    const host = target.getRootNode().host;
    const date = dayjs(host.date).endOf('day');
    const currentValue = date.get(target.id).toString();
    if (currentValue !== target.value) {
      target.value = currentValue || '';
    }
  }
}

function validateDate(date: string) {
  return dayjs(date, 'YYYY-MM-DD', true).isValid();
}

function handleInputChange(event: any) {
  const host = event.currentTarget.getRootNode().host;
  const proposedDateStr = ['year', 'month']
    .map(unit => {
      const value = host.shadowRoot.getElementById(unit).value;
      return unit === 'year' ? value.padStart(4, '0') : value.padStart(2, '0');
    })
    .join('-');

  const viewDate = dayjs(proposedDateStr);
  if (viewDate.isValid()) {
    host.pauseRerender = true;
    host.start = host.end = viewDate.format('YYYY-MM');
    host.pauseRerender = false;
    host.buildCalendarMonths();
  }
}

// Because the input element has a type of "number" it automatically prevents almost all non-numeric keys from being entered. This simply prevents ALL non-numeric chararcters from being entered into the field, since they are meaningless with dates.
function handleKeyDown(event: KeyboardEvent) {
  if (/^[.,eE]$/.test(event.key)) event.preventDefault();
}

// Prevent navigation buttons from taking focus away from input fields.
function handleFilteredMouseDown(event: MouseEvent) {
  event.preventDefault();
}

// Month and Year pickers prevent handleFilteredMouseDown since they are select/input fields that require focus for interaction.
function handleAllowedMouseDown(event: MouseEvent) {
  event.stopPropagation();
}

function handleClick(event: any) {
  // Prevent Default so that when aha-datepicker is used within a label,
  // the click event does not recur on the label and apply to aha-datepicker's menu button and close the datepicker.
  event.preventDefault();
  const host = event.currentTarget;
  const td = event.composedPath().find((elem: any) => elem.tagName === 'TD');
  if (!td) return;

  host.setDate(td.dataset.isoDate);
}

function handleKeyup(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    const target = event.currentTarget as HTMLTableCellElement;
    // @ts-ignore
    const host = target.getRootNode().host;
    // @ts-ignore
    if (host) {
      host.setDate(target.dataset.isoDate);

      requestAnimationFrame(() => {
        // Wait a tick to focus, because if we have switched to a new month we want the focus to be on the correct <td>.
        host.shadowRoot.querySelector(`[data-iso-date="${host.date}"]`).focus();
      });
    }
  }
}

function handleDateClickEvent(event: MouseEvent) {
  // @ts-ignore
  const host = event.target.getRootNode().host;
  // @ts-ignore
  const td = event.composedPath().find(node => node.tagName && node.tagName === 'TD');
  if (td) host.dateClickedEvent.emit({ element: td, host: host });
}

function goToToday(event: any) {
  const host = event.currentTarget.getRootNode().host;
  const today = dayjs();
  host.setDate(today.format('YYYY-MM-DD'));

  if (host.monthsBetweenDates === 1) {
    host.start = host.end = today.format('YYYY-MM');
  } else {
    // If the host.date is currently viewable, then do nothing.
    if (host.start < host.date && host.date < host.end) return;

    // Otherwise make the view such that the host.date is somewhere in the middle.
    const monthsBetweenDates = host.monthsBetweenDates; // Capture the original value before we change anything.
    const start = today.subtract(Math.floor((monthsBetweenDates - 1) / 2), 'months');
    host.start = start.format('YYYY-MM');
    host.end = start.add(monthsBetweenDates - 1, 'months').format('YYYY-MM');
  }
}

function jumpToPreviousMonth(event: any) {
  const host = event.target.getRootNode().host;
  const previousMonth = dayjs(host.start).subtract(1, 'month');
  host.start = host.end = previousMonth.format('YYYY-MM');
}

function jumpToNextMonth(event: any) {
  const host = event.target.getRootNode().host;
  const nextMonth = dayjs(host.start).add(1, 'month');
  host.start = host.end = nextMonth.format('YYYY-MM');
}

function jumpToPreviousRow(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target) return;

  // @ts-ignore
  const host = target.getRootNode().host;
  skipMonths(host, 'subtract', host.monthsToSkipBy);
}

function jumpToPreviousPage(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target) return;

  // @ts-ignore
  const host = target.getRootNode().host;
  skipMonths(host, 'subtract', host.monthsBetweenDates);
}

function jumpToNextRow(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target) return;

  // @ts-ignore
  const host = target.getRootNode().host;
  skipMonths(host, 'add', host.monthsToSkipBy);
}

function jumpToNextPage(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target) return;

  // @ts-ignore
  const host = target.getRootNode().host;
  skipMonths(host, 'add', host.monthsBetweenDates);
}

function skipMonths(host: HTMLAhaCalendarElement, method: string, num: number) {
  if (!method) return 0;
  // @ts-ignore
  host.start = dayjs(host.start)[method](num, 'month').format('YYYY-MM');
  // @ts-ignore
  host.end = dayjs(host.end)[method](num, 'month').format('YYYY-MM');
}

export interface AhaCalendar extends HTMLAhaCalendarElement {}

/**
 * A component which generates either a calendar for a single month, or, by specifying different `start` and `end` dates, generates a calendar spanning multiple months.
 *
 * Can be used in conjunction with the `<aha-calendar-event>` component to automatically generate events with colored dots for specific dates, and explanatory tooltips for those dates outlining the details of the event.
 */
@Component({
  tag: 'aha-calendar',
  styleUrl: 'aha-calendar.css',
  shadow: true,
})
export class AhaCalendar {
  @Element() host!: HTMLAhaCalendarElement;

  /** The selected date that is highlighted in the calendar. Should be of form `YYYY-MM-DD`. If not set, the calendar view will default to today's month and year. */
  @Prop({ reflect: true }) date: string | undefined;

  private setDate(date: string | undefined) {
    this.date = date;
    if (this.date) {
      const date = dayjs(this.date);
      if (!validateDate(this.date)) this.date = date.format('YYYY-MM-DD');

      if (this.start === this.end) {
        // Changing a value where the new value is vastly different than the old value can cause the UI to freeze, as the `start` and `end` values will be very different for a brief moment and it thinks we are trying to generate lots of different calendars instead of just a single one. To fix this, we pause rendering and then re-render when it is done.
        this.pauseRerender = true;
        this.start = this.end = date.format('YYYY-MM');
        this.pauseRerender = false;
      }
    }
    this.buildCalendarMonths();
    this.selectEvent.emit({ element: this.host, date: this.date });
  }

  /** The starting year and month of the calendar. If empty, defaults to the selected date's year and month. */
  @Prop({ reflect: true }) start!: string;

  /** The ending year and month of the calendar. If empty, defaults to the selected date's year and month. */
  @Prop({ reflect: true }) end!: string;

  @Watch('start')
  @Watch('end')
  private validateYearMonth(newValue: any, oldValue: any, prop: any) {
    if (!this.pauseRerender) {
      if (/^\d{1,4}-\d{2}$/.test(newValue)) {
        const value = dayjs(newValue);
        if (!value.isValid()) {
          // @ts-ignore
          this[prop] = value.format('YYYY-MM');
        }

        // Wait a tick to see if both `start` and `end` have been set, and if so, then re-render. This works around a performance issue when `start` and `end` are set at the same time, and it's possible that when the first value is set that there is a large difference between the two values, which causes the UI to freeze for a moment. Pausing for just a tick to ensure that both values are set before re-rendering fixes this.
        requestAnimationFrame(() => {
          this.buildCalendarMonths();
        });
      } else {
        throw new Error(`The "${prop}" attribute should be a date string in the format "YYYY-MM".`);
      }
    }
    this.viewChangedEvent.emit({ element: this.host, start: this.start, end: this.end });
  }

  /** Whether or not date change controls appear in the component (ex, previous and next month, etc.) */
  @Prop({ reflect: true }) controls: boolean = false;

  /** Whether or not a tooltip will appear when dates with embedded `<aha-calendar-event>` components are hovered over. */
  @Prop({ reflect: true }) tooltips: boolean = false;

  /** Whether or not all the possible calendar months will have a consistent number of rows. By default, the calendar only shows as many rows as it needs to in order to contain all the dates in a calendar month. When used within `<aha-datepicker>` we want all months to have the same number of rows so that the month's size does not jump around when the "previous" or "next" buttons are clicked. Setting `uniform` to true will cause all months to be displayed with a consistent number of rows of dates (6). */
  @Prop({ reflect: true }) uniform?: boolean;

  @Watch('controls')
  @Watch('tooltips')
  @Watch('uniform')
  private rebuildCalendarMonths() {
    this.buildCalendarMonths();
  }

  // State variable which keeps the rendered view of calendar tables. When this changes (ex, dates have changed) the component will automatically re-render.
  @State() calendarMonths: any;

  @State() monthsToSkipBy = 0;

  @State() pauseRerender = false;

  private getMonthsToSkipBy() {
    if (!this.host.shadowRoot) {
      requestAnimationFrame(() => this.getMonthsToSkipBy());
      return;
    }

    const tops = Array.from(this.host.shadowRoot.querySelectorAll('table')).map((node: any) => {
      return node.getBoundingClientRect().top;
    });
    const firstValue = tops[0] || 0;
    let skipBy = 0;
    for (let i = 0; i < tops.length; i++) {
      if (tops[i] !== firstValue) break;
      skipBy++;
    }
    this.monthsToSkipBy = skipBy;
  }

  get monthsBetweenDates() {
    const start = this.start < this.end ? this.start : this.end;
    const end = this.end > this.start ? this.end : this.start;
    const count = Math.ceil(
      dayjs(end).endOf('month').diff(dayjs(start).startOf('month'), 'month', true),
    );

    return isNaN(count) ? 0 : count;
  }

  get showIndividualMonthControls() {
    return this.controls && this.monthsBetweenDates === 1;
  }

  get showCalendarControls() {
    return this.controls && this.monthsBetweenDates !== 1;
  }

  /**
   * Triggers when a calendar date is selected.
   */
  @Event({
    eventName: 'aha-calendar:selected',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  selectEvent!: EventEmitter;

  /**
   * Triggers when a calendar date is clicked via mouse/trackpad. While similar to the `aha-calendar:selected` event, this event is specifically fired when the date is **clicked**.
   */
  @Event({
    eventName: 'aha-calendar:date-clicked',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  dateClickedEvent!: EventEmitter;

  /**
   * Triggers when a calendar viewable date range is changed.
   *
   * _Note:_ This event will be fired twice if both the `start` and `end` dates are changed.
   */
  @Event({
    eventName: 'aha-calendar:view-changed',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  viewChangedEvent!: EventEmitter;

  private handleMouseEnter(event: MouseEvent) {
    const target = event.target as HTMLTableCellElement;
    if (target) {
      const slot = target.querySelector(
        `:scope slot[name="${target.dataset.isoDate}"]`,
      ) as HTMLSlotElement;
      const tooltip = target.querySelector(':scope aha-tooltip.date-tooltip');
      if (!slot || !tooltip) return;

      const elems = slot.assignedElements();
      // @ts-ignore
      if (elems.every((node: HTMLElement) => node.tagName === 'SLOT')) {
        const slottedElems = elems
          // @ts-ignore
          .map((node: HTMLSlotElement) => {
            return node.assignedElements();
          })
          .flat(Infinity);

        if (slottedElems.length) {
          tooltip.removeAttribute('disabled');
        } else {
          tooltip.setAttribute('disabled', '');
        }
        // @ts-ignore
      } else if (elems.length) {
        tooltip.removeAttribute('disabled');
      } else {
        tooltip.setAttribute('disabled', '');
      }
    }
  }

  private async buildCalendarMonths() {
    const selected = dayjs(this.date).endOf('day');
    const today = dayjs().endOf('day');
    const todayIso = today.format('YYYY-MM-DD');
    const start = dayjs(this.start).startOf('month');

    const view = this.controls && this.start === this.end ? dayjs(this.start) : selected;

    const months: any[] = [];
    [...Array(this.monthsBetweenDates).keys()].map(async (_, i) => {
      const date = start.add(i, 'month');
      const month = calendarDates.getMatrix(dayjs(date).toDate()).then((calendar: any) => {
        if (this.uniform) return calendar;

        return calendar.filter((row: any) => {
          // Ensure that the calendar does not have any rows without a date from the current month.
          return row.some((cell: any) => cell.type === 'current');
        });
      });

      months.push(month);
    });

    this.calendarMonths = await Promise.all(months).then(values => {
      return values.map((arr: any[], i: number) => {
        return (
          <table cellSpacing={0}>
            <caption>
              <div id="caption-inner">
                <div id="month-year">
                  <div class="input-container">
                    {this.showIndividualMonthControls ? (
                      <select
                        id="month"
                        data-unit="month"
                        onChange={handleInputChange}
                        onMouseDown={handleAllowedMouseDown}
                      >
                        {calendarMonths.map((month, i) => {
                          return (
                            <option value={i + 1} selected={month === calendarMonths[view.month()]}>
                              {month}
                            </option>
                          );
                        })}
                      </select>
                    ) : null}
                    <div>{start.add(i, 'month').format('MMMM')}</div>
                  </div>
                  <div class="input-container">
                    {this.showIndividualMonthControls ? (
                      <input
                        id="year"
                        autoComplete="off"
                        type="number"
                        value={view.year()}
                        placeholder={today.year().toString()}
                        onKeyDown={handleKeyDown}
                        onInput={handleInputChange}
                        onBlur={setCalendarDefaultValue}
                        onMouseDown={handleAllowedMouseDown}
                        minLength={1}
                        maxLength={4}
                        min={0}
                      />
                    ) : null}
                    <div>{start.add(i, 'month').format('YYYY')}</div>
                  </div>
                </div>
                {this.showIndividualMonthControls ? (
                  <div id="month-controls" class="controls">
                    <aha-button
                      id="jump-to-previous-month"
                      size="mini"
                      type="button"
                      class="arrow-button"
                      part="arrow-button"
                      onClick={jumpToPreviousMonth}
                      kind="ghost"
                    >
                      <aha-icon icon="far fa-angle-left"></aha-icon>
                    </aha-button>
                    <aha-tooltip>
                      <span class="tooltip-body">Jump to previous month</span>
                    </aha-tooltip>
                    <aha-button
                      size="mini"
                      id="jump-to-today"
                      part="jump-to-today"
                      slot="trigger"
                      type="button"
                      onClick={goToToday}
                      title="Select today's date"
                      kind="ghost"
                    >
                      <aha-icon icon="far fa-calendar-day"></aha-icon>
                    </aha-button>
                    <aha-tooltip>
                      <span class="tooltip-body">Jump to todayʼs date</span>
                    </aha-tooltip>
                    <aha-button
                      size="mini"
                      id="jump-to-next-month"
                      slot="trigger"
                      type="button"
                      class="arrow-button"
                      part="arrow-button"
                      onClick={jumpToNextMonth}
                      kind="ghost"
                    >
                      <aha-icon icon="far fa-angle-right"></aha-icon>
                    </aha-button>
                    <aha-tooltip>
                      <span class="tooltip-body">Jump to next month</span>
                    </aha-tooltip>
                  </div>
                ) : null}
              </div>
            </caption>
            <thead>
              <tr>
                <th>
                  <abbr title="Sunday">Su</abbr>
                </th>
                <th>
                  <abbr title="Monday">Mo</abbr>
                </th>
                <th>
                  <abbr title="Tuesday">Tu</abbr>
                </th>
                <th>
                  <abbr title="Wednesday">We</abbr>
                </th>
                <th>
                  <abbr title="Thursday">Th</abbr>
                </th>
                <th>
                  <abbr title="Friday">Fr</abbr>
                </th>
                <th>
                  <abbr title="Saturday">Sa</abbr>
                </th>
              </tr>
            </thead>
            <tbody>
              {arr.map((week: [Array<object>]) => {
                return (
                  <tr>
                    {week.map((day: object) => {
                      // @ts-ignore
                      const { date, type, iso } = day;
                      const isCurrentDate = iso === todayIso;
                      const title = isCurrentDate
                        ? `Today is ${new Intl.DateTimeFormat(navigator.language, {
                            dateStyle: 'full',
                          }).format(today.toDate())}`
                        : '';

                      if (
                        !this.uniform &&
                        type !== 'current' &&
                        (!this.controls || this.monthsBetweenDates > 1)
                      ) {
                        return <td></td>;
                      } else {
                        let dateNumber;
                        if (this.tooltips) {
                          dateNumber = (
                            <div>
                              <div class="date-number">
                                <div>{date}</div>
                                <div class="event-dots">
                                  <slot name={`event-dot-${iso}`}></slot>
                                </div>
                              </div>
                              <aha-tooltip disabled class="date-tooltip">
                                <div class="event-contents">
                                  <div
                                    style={{
                                      whiteSpace: 'nowrap',
                                      borderBottom: '1px solid currentColor',
                                      paddingBottom: 'var(--spacer-1)',
                                      marginBottom: 'var(--spacer-1)',
                                    }}
                                  >
                                    <aha-date date={iso} format="full"></aha-date>
                                  </div>
                                  <slot name={iso}></slot>
                                </div>
                              </aha-tooltip>
                            </div>
                          );
                        } else {
                          dateNumber = (
                            <div class="date-number">
                              <div>{date}</div>
                              <div class="event-dots">
                                <slot name={`event-dot-${iso}`}></slot>
                              </div>

                              <slot name={iso}></slot>
                            </div>
                          );
                        }

                        return (
                          <td
                            data-iso-date={iso}
                            data-value={date}
                            data-type={type}
                            data-previous={type === 'previous'}
                            data-next={type === 'next'}
                            data-selected={iso === this.date}
                            data-today={isCurrentDate}
                            title={title}
                            tabindex={0}
                            onKeyUp={handleKeyup}
                            onMouseEnter={this.handleMouseEnter}
                            onClick={handleDateClickEvent}
                          >
                            {dateNumber}
                          </td>
                        );
                      }
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      });
    });
    requestAnimationFrame(() => this.getMonthsToSkipBy());
  }

  connectedCallback() {
    if (this.date) {
      const date = dayjs(this.date);
      if (!this.start) this.start = date.format('YYYY-MM');
      if (!this.end) this.end = date.format('YYYY-MM');
    } else {
      const today = dayjs();
      if (!this.start) this.start = today.format('YYYY-MM');
      if (!this.end) this.end = today.format('YYYY-MM');
    }

    this.buildCalendarMonths();
  }

  render() {
    return (
      <Host onClick={handleClick} onMouseDown={handleFilteredMouseDown}>
        {this.showCalendarControls ? (
          <div>
            <div id="calendar-controls" class="controls">
              <aha-button
                size="mini"
                type="button"
                class="arrow-button"
                part="arrow-button"
                onClick={jumpToPreviousPage}
                kind="ghost"
              >
                <aha-icon icon="far fa-angles-left"></aha-icon>
              </aha-button>
              <aha-tooltip>
                <span class="tooltip-body">Show previous {this.monthsBetweenDates} months</span>
              </aha-tooltip>
              <aha-button
                size="mini"
                type="button"
                class="arrow-button"
                part="arrow-button"
                onClick={jumpToPreviousRow}
                kind="ghost"
              >
                <aha-icon icon="far fa-angle-left"></aha-icon>
              </aha-button>
              <aha-tooltip>
                <span class="tooltip-body">Show previous {this.monthsToSkipBy} months</span>
              </aha-tooltip>
              <aha-button
                size="mini"
                id="jump-to-today"
                part="jump-to-today"
                type="button"
                onClick={goToToday}
                title="Select today's date"
                kind="ghost"
              >
                <aha-icon icon="far fa-calendar-day"></aha-icon>
              </aha-button>
              <aha-tooltip>
                <span class="tooltip-body">Jump to todayʼs date</span>
              </aha-tooltip>
              <aha-button
                size="mini"
                type="button"
                class="arrow-button"
                part="arrow-button"
                onClick={jumpToNextRow}
                kind="ghost"
              >
                <aha-icon icon="far fa-angle-right"></aha-icon>
              </aha-button>
              <aha-tooltip>
                <span class="tooltip-body">Show next {this.monthsToSkipBy} months</span>
              </aha-tooltip>
              <aha-button
                size="mini"
                type="button"
                class="arrow-button"
                part="arrow-button"
                onClick={jumpToNextPage}
                kind="ghost"
              >
                <aha-icon icon="far fa-angles-right"></aha-icon>
              </aha-button>
              <aha-tooltip>
                <span class="tooltip-body">Show next {this.monthsBetweenDates} months</span>
              </aha-tooltip>
            </div>
          </div>
        ) : null}
        <div id="months">{this.calendarMonths}</div>
      </Host>
    );
  }
}

/* TODO: Days should be localized */
/* TODO: Order of days of week should be localized (Sun-Sat) or (Mon-Sun) */
